import { SidebarAvatar, SidebarMenuButton } from '@/design-system/sidebar';
import { Stack } from '@/design-system/stack/stack';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuSub,
  DropdownMenuSubContent,
  DropdownMenuSubTrigger,
  DropdownMenuTrigger,
} from '@/design-system/ui/dropdown-menu';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { Check, ChevronsUpDown } from 'lucide-react';
import { AvatarNameDescription } from '../../../design-system/avatar/avatar-name-description/avatar-name-description';
import { Badge } from '../../../design-system/badge/badge';
import { Inline } from '../../../design-system/inline/inline';
import { supportedCountryCurrencies, SupportedCountryCurrency } from '../../../types/supported-country-currency';
import { getCurrencyFlag } from '../../../utils/get-currency-flag';

type AvatarDropdownProps = {
  avatarUrl?: string;
  name?: string;
  email?: string;
  baseCurrency?: SupportedCountryCurrency;
  onCurrencyChange?: (currency: SupportedCountryCurrency) => void;
  onTeamClick: () => void;
  onSwitchOrgClick: () => void;
  onIntegrationsClick: () => void;
  logout: () => void;
};

const AvatarDropdown = ({
  avatarUrl,
  name,
  email,
  baseCurrency,
  onCurrencyChange,
  onTeamClick,
  onSwitchOrgClick,
  onIntegrationsClick,
  logout,
}: AvatarDropdownProps) => {
  const { isLongXeroFlowLive } = useFlags();

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button type="button" className="w-full">
          <SidebarMenuButton size="lg" aria-label="Open user account menu" className="bg-sidebar-accent" asChild>
            <span>
              <SidebarAvatar>
                <img
                  src={avatarUrl}
                  alt=""
                  loading="eager"
                  fetchpriority="high"
                  className="h-full w-full rounded-full object-cover"
                />
              </SidebarAvatar>
              <span className="flex-1 truncate text-left text-sm font-semibold">{name}</span>
              <ChevronsUpDown className="size-4 text-muted-foreground" />
            </span>
          </SidebarMenuButton>
        </button>
      </DropdownMenuTrigger>

      <DropdownMenuContent align="start" side="top" className="w-80 p-0">
        <Inline className="rounded-t-xl bg-muted p-5" justify="between">
          <AvatarNameDescription
            className="max-w-52"
            name={name ?? ''}
            avatar={avatarUrl ? { showAvatar: true, avatarUrl, size: 'sm' } : { showAvatar: false }}
            description={email ?? ''}
          />
          <Badge variant="neutral">Admin</Badge>
        </Inline>

        <div className="p-1">
          {onCurrencyChange && (
            <>
              <DropdownMenuSub>
                <DropdownMenuSubTrigger className="gap-2">
                  <span>Preferred currency</span>
                </DropdownMenuSubTrigger>
                <DropdownMenuSubContent>
                  {supportedCountryCurrencies.map((currency) => {
                    const isSelected = currency === baseCurrency;
                    const flag = getCurrencyFlag(currency);

                    return (
                      <DropdownMenuItem key={currency} onClick={() => onCurrencyChange(currency)} className="gap-2">
                        <div className="h-4 w-4 overflow-clip rounded-full">
                          <img src={flag} alt="" className="h-full w-full" />
                        </div>
                        <span className="flex-1">{currency}</span>
                        {isSelected && <Check className="h-4 w-4" />}
                      </DropdownMenuItem>
                    );
                  })}
                </DropdownMenuSubContent>
              </DropdownMenuSub>

              <DropdownMenuSeparator />
            </>
          )}

          <Stack space={1} align="stretch">
            <DropdownMenuItem onClick={onTeamClick} className="gap-2 text-sm font-normal text-popover-foreground">
              <span>Team</span>
            </DropdownMenuItem>

            <DropdownMenuItem onClick={onSwitchOrgClick} className="gap-2 text-sm font-normal text-popover-foreground">
              <span>Switch Organisation</span>
            </DropdownMenuItem>

            {isLongXeroFlowLive && (
              <DropdownMenuItem
                onClick={onIntegrationsClick}
                className="gap-2 text-sm font-normal text-popover-foreground"
              >
                <span>Integrations</span>
              </DropdownMenuItem>
            )}

            <DropdownMenuSeparator />

            <DropdownMenuItem onClick={logout} className="gap-2 text-sm font-normal text-popover-foreground">
              <span>Log Out</span>
            </DropdownMenuItem>
          </Stack>
        </div>
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

export default AvatarDropdown;
